import { Injectable } from '@angular/core';
import { finalize, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AppMainService } from '../../../../app-main.service';
import { GelAllAccountRequestModel } from '../../../../opportunities-v2/model/account-request.model';
import { AccountServiceN } from '../../../../accounts-v2/services/account.services';
import { AccountService } from '../../../../accounts/accountService';
import { Channel } from '../../../../accounts-v2/models/configuration/channel.model';
import { WhatsAppService } from '../../services/whatsapp.service';
import { GetAllLeadRequestModel } from '../../../../leads-v2/model/lead-request.model';
import { LeadService } from '../../../../leads-v2/leadService';
import { LeadServiceN } from '../../../../leads-v2/services/lead.service';
import { ContactService } from '../../../../contacts/contactService';
import { GelAllContactRequestModel } from '../../../../modules/shared/models/filter.model';

@Injectable()
export class ChatFacade {
  constructor(
    private appMainService: AppMainService,
    private accountServiceN: AccountServiceN,
    private accountService: AccountService,
    private leadService: LeadService,
    private leadServiceN: LeadServiceN,
    private contactService: ContactService,
    private whatsAppService: WhatsAppService,
    private messageService: MessageService
  ) {}

  getBusinessAccounts() {
    return this.whatsAppService
      .getBusinessAccounts()
      .pipe(catchError(() => []));
  }

  getMessageTemplates() {
    return this.whatsAppService.getMessageTemplates().pipe(
      map((templates) => {
        templates = (templates ?? []).filter((t) => t.status == 'APPROVED');
        return templates;
      }),
      catchError(() => [])
    );
  }

  sentTemplateMessage(templateMessage: any): Observable<any> {
    return this.whatsAppService.setTemplateMessage(templateMessage).pipe(
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.message
            ? error?.error?.message
            : 'Failed to sent template message...',
        });
        return of(null);
      })
    );
  }

  searchAccount(accountName: string = '', selectedChannel: Channel) {
    if (!accountName && selectedChannel) {
      of([]);
    }

    const requestModel: GelAllAccountRequestModel = {
      channelId: selectedChannel.id,
      stageId: null,
      isActive: true,
      globalSearchValue: accountName,
      selectableColumns: [
        { columnName: 'businessName', isCustom: false },
        { columnName: 'sequenceNumber', isCustom: false },
        { columnName: 'nameWithSequenceNumber', isCUstom: false },
      ],
      filter: null,
      pageSize: 0,
      pageNumber: 0,
      sortByColumn: null,
      isSortByDescending: true,
      filterInColumns: [],
    };

    this.appMainService.isShowSpinner('getAccountList');

    return this.accountServiceN.getAccountsList(requestModel).pipe(
      map((response) =>
        response?.list?.length > 0 ? response?.list.slice(0, 1000) : []
      ),
      catchError(() => []),
      finalize(() => this.appMainService.isHideSpinner('getAccountList'))
    );
  }

  getAccountContacts(account: any) {
    this.appMainService.isShowSpinner('GetDetails');
    return this.accountService.getAccountContactNameList(account.id).pipe(
      map((response) => response.filter((el) => el && el.contact)),
      catchError(() => of([])),
      finalize(() => this.appMainService.isHideSpinner('GetDetails'))
    );
  }

  searchLeads(channelId: number, leadName: string = '') {
    const requestModel: GetAllLeadRequestModel = {
      channelId: channelId,
      stageId: null,
      isActive: true,
      globalSearchValue: leadName,
      selectableColumns: [
        {
          columnName: 'businessName',
          isCustom: false,
        },
      ],
      filter: null,
      pageSize: 100,
      pageNumber: 1,
      sortByColumn: null,
      isSortByDescending: true,
      filterInColumns: [],
    };

    return this.leadServiceN.getLeadsList(requestModel);
  }

  getLeadDetails(leadId: number) {
    return this.leadService.getLeadDetails(leadId);
  }

  getContactList(channelId: number, associationTypeName = 'organization', globalSearchValue = '') {
    const requestModel: GelAllContactRequestModel = {
      associationTypeName: associationTypeName,
      isActive: true,
      globalSearchValue: globalSearchValue,
      filter: 'allcontacts',
      pageSize: 0,
      pageNumber: 0,
      sortByColumn: null,
      isSortByDescending: false,
      filterableColumns: [
          {name: "contactAssociation", value: associationTypeName, operator: "contains"}
      ],
      globalChannelId: channelId,
      selectableColumns: [
        { columnName: 'fullName', isCustom: false },
        { columnName: 'phone', isCustom: false },
        { columnName: 'email', isCustom: false, },
        { columnName: 'contactType', isCustom: false },
        { columnName: 'contactAssociation', isCustom: false },
      ],
    };

    return this.contactService.getContactListNew(requestModel);
  }
}
